import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDateTime } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

type Quelle = 'bewegung' | 'aenderung' | 'anmeldung' | 'zugriff';

interface Protokollzeile {
  quelle: Quelle;
  zeitpunkt: string;
  benutzer: string | null;
  bereich: string;
  vorgang: string;
  beschreibung: string;
  details: string | null;
}

const QUELLEN: { schluessel: Quelle; label: string; erklaerung: string }[] = [
  {
    schluessel: 'bewegung',
    label: 'Bestand',
    erklaerung: 'Jede Bestandsbewegung — unveränderlich, die Grundaufzeichnung des Lagers',
  },
  {
    schluessel: 'aenderung',
    label: 'Änderungen',
    erklaerung: 'Stammdaten, Tarife, Belegstatus, Benutzer',
  },
  { schluessel: 'anmeldung', label: 'Anmeldungen', erklaerung: 'Erfolge und Fehlversuche' },
  {
    schluessel: 'zugriff',
    label: 'Zugriffe',
    erklaerung: 'Belegabrufe und Zugriffe des Prüferzugangs',
  },
];

const QUELLE_FARBE: Record<Quelle, 'default' | 'ok' | 'warn' | 'bad' | 'info'> = {
  bewegung: 'ok',
  aenderung: 'info',
  anmeldung: 'default',
  zugriff: 'warn',
};

const vorTagen = (tage: number) =>
  new Date(Date.now() - tage * 86_400_000).toISOString().slice(0, 10);

export function ProtocolPage() {
  const [zeilen, setZeilen] = useState<Protokollzeile[] | null>(null);
  const [benutzerListe, setBenutzerListe] = useState<string[]>([]);

  const [filter, setFilter] = useState({
    von: vorTagen(7),
    bis: vorTagen(0),
    benutzer: '',
    suche: '',
  });
  const [quellen, setQuellen] = useState<Quelle[]>(['bewegung', 'aenderung', 'anmeldung', 'zugriff']);

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /** Die Filterwerte als Abfrageparameter - einmal definiert, zweimal genutzt. */
  const parameter = useCallback(
    () => ({
      von: filter.von || undefined,
      bis: filter.bis || undefined,
      benutzer: filter.benutzer || undefined,
      suche: filter.suche || undefined,
      quellen: quellen.length > 0 ? quellen.join(',') : 'keine',
      limit: '400',
    }),
    [filter, quellen],
  );

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const daten = await api<{ zeilen: Protokollzeile[]; benutzer: string[] }>(
        '/api/system/protokoll',
        { query: parameter() },
      );
      setZeilen(daten.zeilen);
      setBenutzerListe(daten.benutzer);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }, [parameter]);

  useEffect(() => {
    void load();
  }, [load]);

  function umschalten(quelle: Quelle) {
    setQuellen(
      quellen.includes(quelle) ? quellen.filter((q) => q !== quelle) : [...quellen, quelle],
    );
  }

  async function exportieren() {
    setError('');
    try {
      const suchparameter = new URLSearchParams(
        Object.entries(parameter()).filter(([, wert]) => wert !== undefined) as [string, string][],
      );
      await downloadFile(`/api/system/protokoll.csv?${suchparameter.toString()}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card
        title="Protokoll"
        actions={
          <>
            <button className="small" onClick={load} disabled={busy}>
              {busy ? 'Lädt …' : 'aktualisieren'}
            </button>
            <button className="small" onClick={exportieren}>
              als CSV
            </button>
          </>
        }
      >
        <div className="small muted" style={{ marginBottom: 12 }}>
          Vier Spuren in einer Ansicht. Das Bewegungsjournal ist unveränderlich und per Datenbank
          gegen Änderung gesperrt — ein Bestand lässt sich gar nicht ändern, ohne dass hier eine
          Zeile entsteht.
        </div>

        <div className="form-grid">
          <TextField
            label="Von"
            type="date"
            value={filter.von}
            onChange={(v) => setFilter({ ...filter, von: v })}
          />
          <TextField
            label="Bis"
            type="date"
            value={filter.bis}
            onChange={(v) => setFilter({ ...filter, bis: v })}
          />
          <label className="field">
            <span>Benutzer</span>
            <select
              value={filter.benutzer}
              onChange={(e) => setFilter({ ...filter, benutzer: e.target.value })}
            >
              <option value="">alle</option>
              {benutzerListe.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <TextField
            label="Suche"
            value={filter.suche}
            onChange={(v) => setFilter({ ...filter, suche: v })}
            placeholder="Artikel, Beleg, Bereich …"
          />
        </div>

        <div className="tabs" style={{ marginTop: 4, marginBottom: 0, borderBottom: 'none' }}>
          {QUELLEN.map((quelle) => (
            <button
              key={quelle.schluessel}
              className={quellen.includes(quelle.schluessel) ? 'active' : ''}
              onClick={() => umschalten(quelle.schluessel)}
              title={quelle.erklaerung}
            >
              {quelle.label}
            </button>
          ))}
        </div>
      </Card>

      <Card
        title={zeilen ? `${zeilen.length} Einträge` : 'Einträge'}
        padded={false}
      >
        {!zeilen ? (
          <Loading />
        ) : zeilen.length === 0 ? (
          <Empty>
            Nichts gefunden. Weiter zurück suchen oder eine Quelle wieder einschalten.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Quelle</th>
                  <th>Benutzer</th>
                  <th>Bereich</th>
                  <th>Vorgang</th>
                  <th>Beschreibung</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((zeile, index) => (
                  <tr key={`${zeile.quelle}-${zeile.zeitpunkt}-${index}`}>
                    <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(zeile.zeitpunkt)}
                    </td>
                    <td>
                      <Badge kind={QUELLE_FARBE[zeile.quelle]}>
                        {QUELLEN.find((q) => q.schluessel === zeile.quelle)?.label ?? zeile.quelle}
                      </Badge>
                    </td>
                    <td className="small">{zeile.benutzer ?? <span className="muted">System</span>}</td>
                    <td className="small">{zeile.bereich}</td>
                    <td className="small">{zeile.vorgang}</td>
                    <td className="small">{zeile.beschreibung}</td>
                    <td className="small muted">{zeile.details ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
